<script setup lang="ts">
import { Ref, onMounted, ref, watchEffect, computed } from 'vue';
import FilterRange from './FilterRange.vue';
import type { SearchFilter, FilterOption, Range } from '../../../search-panel-types';
import FilterChip from './FilterChip.vue';
import useLocale from '../../../composables/useLocale';
import FilterFieldLayout from './FilterFieldLayout.vue';

type Emits = {
  (e: 'filters-updated', filter: SearchFilter): void;
  (e: 'filter-removed', filterKey: string, value: FilterOption): void;
};
const emit = defineEmits<Emits>();

type Props = {
  items: FilterOption[];
  fieldKey: string;
  label: string;
  selected: { value: SearchFilter };
  ranges: Range[] | [];
};
const props = defineProps<Props>();
const { t } = useLocale();

const selectedDurationChip = ref<string>('');
const selectedPagesChip = ref<string>('');
const selectedAgesChip = ref<string>('');
const selectedLevelChip = ref<string>('');
const selectedInternal: Ref<FilterOption[]> = ref([]);
const multiplesRanges = ref<Range[]>(props.ranges);
const currentRange: Ref<number[] | null> = ref(null);
const filterMax = ref<number>(0);

const findMaxValue = () => {
  filterMax.value =
    props.items.reduce(
      (prev: number | null, cur: FilterOption) => (Number(prev) > Number(cur.value) ? prev : Number(cur.value)),
      null,
    ) ?? 0;
};

const rangesData = computed(() =>
  multiplesRanges.value.map((multipleRange: Range) =>
    multipleRange.max === Number.MAX_SAFE_INTEGER ? { ...multipleRange, max: filterMax.value } : multipleRange,
  ),
);
const rangesDataFiltered = computed(() => rangesData.value.filter((r) => r.count > 0));

const getCount = () => {
  props.items.forEach(({ value, count }: FilterOption) => {
    const numValue = parseInt(value as string, 10);
    multiplesRanges.value.forEach((range, index) => {
      if (numValue >= range.min && numValue <= range.max) {
        multiplesRanges.value[index].count += count;
      }
    });
  });
};

onMounted(() => {
  getCount();
  findMaxValue();
});

watchEffect(() => {
  if (!props.selected.value[props.fieldKey]) {
    selectedInternal.value = [];
    currentRange.value =
      rangesDataFiltered.value.length > 0
        ? [rangesDataFiltered.value[0].min, rangesDataFiltered.value[rangesDataFiltered.value.length - 1].max]
        : [];
    return;
  }
  selectedInternal.value = props.selected.value[props.fieldKey];
});

// TODO: Add type for value instead of Any as we should know what can be included in Range And Multiple
const isSelected = (value: any): boolean => {
  if (selectedInternal.value.length === 2) {
    return selectedInternal.value[0].value === value;
  }
  return !!selectedInternal.value.find((internal) => internal.value === value);
};

// TODO: Add type for filter instead of Any as we should know what can be included in Range And Multiple
const handleFilterUpdate = (filter: any) => {
  const f = {
    min: filter.min ?? filter[props.fieldKey][0].value,
    max: filter.max ?? filter[props.fieldKey][1].value,
    count: 1,
  };
  let filterValue;
  if (props.fieldKey === 'duration') {
    filterValue = {
      [props.fieldKey]: [
        {
          value: f.min,
          label: `${f.max === filterMax.value ? `${Math.round(f.min / 60)}` : `${Math.round(f.min / 60)}-`}${
            f.max === filterMax.value ? '+' : `${Math.round(f.max / 60)}`
          } ${t(`filter.${props.fieldKey}`)}`,
          count: 1,
        },
        { value: f.max, label: '', count: 1 },
      ],
    };
  } else if (props.fieldKey === 'properties.readabilityMetrics.iltLevel') {
    filterValue = {
      [props.fieldKey]: [
        {
          value: f.min,
          label: `${t('filter.level')} ${f.min}-${f.max}`,
          count: 1,
        },
        { value: f.min, label: '', count: 1 },
      ],
    };
  } else {
    filterValue = {
      [props.fieldKey]: [
        {
          value: f.min,
          label: `${f.max === filterMax.value ? `${f.min}` : `${f.min}-`}${
            f.max === filterMax.value ? '+' : `${f.max}`
          } ${t(`filter.${props.fieldKey}`)}`,
          count: 1,
        },
        {
          value: f.max,
          label: '',
          count: 1,
        },
      ],
    };
  }
  emit('filters-updated', filterValue);
  currentRange.value = [f.min, f.max];
};

// TODO: Add type for select instead of Any as we should know what can be included in Range And Multiple
const handleRemoveSelected = (select: any) => {
  emit('filters-updated', {
    [props.fieldKey]: [],
  });
  currentRange.value = [];
  selectedDurationChip.value = '';
  selectedPagesChip.value = '';
  selectedAgesChip.value = '';
  selectedLevelChip.value = '';
  selectedInternal.value = selectedInternal.value.filter((internal) => internal.value !== select.value);
};

const durationLabel = (time: number) => {
  let label = '';
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;

  if (minutes >= 1) {
    label += `${minutes}m`;
  }
  label += `${seconds}s`;
  return label;
};
</script>

<template>
  <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
    <template v-if="props.items?.length > 0" #content>
      <FilterRange
        :items="props.items"
        :field-key="props.fieldKey"
        :label="props.label"
        :selected="props.selected"
        :multiple="true"
        :custom-range="currentRange"
        custom-label
        class="mb-1 pr-5"
        @filters-updated="handleFilterUpdate"
      >
        <template #default="props">
          <p v-if="fieldKey === 'duration'">{{ durationLabel(props.value) }}</p>
          <p v-else>{{ props.value }}</p>
        </template>
      </FilterRange>

      <div v-if="fieldKey === 'duration'" class="d-flex flex-wrap mb-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min / 60 : chip.min / 60 + '-'}${
              chip.max === filterMax ? '+' : chip.max / 60
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="chip.count"
            :is-selected="isSelected(chip.min)"
            @click:close="handleRemoveSelected(chip)"
            @click="handleFilterUpdate(chip)"
          />
        </div>
      </div>
      <div v-else-if="fieldKey === 'properties.readabilityMetrics.iltLevel'" class="d-flex flex-wrap mb-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.min"
            :label="`${t('filter.level')} ${chip.min}`"
            :count="chip.count"
            :is-selected="isSelected(chip.min)"
            @click:close="handleRemoveSelected(chip)"
            @click="handleFilterUpdate(chip)"
          />
        </div>
      </div>
      <div v-else-if="fieldKey === 'ages'" class="d-flex flex-wrap mb-2">
        <div v-for="chip in rangesData" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min : chip.min + '-'}${
              chip.max === filterMax ? '+' : chip.max
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="''"
            :is-selected="isSelected(chip.min)"
            @click:close="handleRemoveSelected(chip)"
            @click="handleFilterUpdate(chip)"
          />
        </div>
      </div>
      <div v-else class="d-flex flex-wrap mb-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min : chip.min + '-'}${
              chip.max === filterMax ? '+' : chip.max
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="fieldKey === 'pages' ? chip.count : ''"
            :is-selected="isSelected(chip.min)"
            @click:close="handleRemoveSelected(chip)"
            @click="handleFilterUpdate(chip)"
          />
        </div>
      </div>
    </template>
  </FilterFieldLayout>
</template>

<style scoped lang="scss"></style>
